import { appStorage, appSession } from '../../lib/safeStorage';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle, Search, ArrowRight, PlayCircle, Award } from 'lucide-react';
import { pageTitles } from '../layout/pageTitles';
import { useRolePreview } from '../../context/RolePreviewContext';
import { readWalkthroughProgress } from '../StaffedProCertificate';
import { GUIDES, guidesForRoute, searchGuides } from '../../lib/guideRegistry';
import { startGuide } from '../../lib/guideStore';

const GOLD = '#D4AF37';

const isAdminLike = (u) => {
  if (!u) return false;
  if (u.role === 'staff' || u.industryType === 'staff' || u.isStaff) return false;
  if (u.role === 'vendor' || u.industryType === 'vendor' || u.isVendor) return false;
  return true;
};

// Pages whose walkthrough is a page-specific SpotlightTour (its own trigger),
// not the PageHeader tour. "Tour this page" must fire the right one.
const PAGE_SPECIFIC_TOURS = {
  '/templates': 'staffed:tour:templates',
  '/qr-codes': 'staffed:tour:qr-codes',
  '/control-center': 'staffed:tour:command-deck',
};

function pageTour(pathname, viewerContext) {
  if (PAGE_SPECIFIC_TOURS[pathname]) return { available: true, trigger: PAGE_SPECIFIC_TOURS[pathname] };
  const entry = pageTitles[pathname];
  if (!entry || entry.hideShowMe) return { available: false, trigger: null };
  const audience = entry.audience || 'admin';
  if (!(audience === 'both' || audience === viewerContext)) return { available: false, trigger: null };
  const tourId = entry.tourId || `page:${pathname}`;
  return { available: true, trigger: `staffed:tour:${tourId}` };
}

const eventIdFromPath = (pathname) => {
  const m = pathname.match(/^\/event\/([^/]+)/);
  return m ? m[1] : null;
};

export default function HelpMenu({ eventName }) {
  const location = useLocation();
  const { effectiveUser } = useRolePreview();
  // BLOCK S7c 4 — on the standalone /event route there is no RolePreviewProvider,
  // so effectiveUser is null and viewerContext fell back to 'staff' → the
  // progress line counted the 7 staff walkthroughs instead of the company-wide
  // 19. Resolve the real signed-in user (from storage) when the context is
  // empty so the count is the same on every page.
  const storedUser = useMemo(() => {
    try { return JSON.parse(appStorage.getItem('user') || appSession.getItem('user') || 'null'); }
    catch { return null; }
  }, []);
  const contextUser = effectiveUser || storedUser;
  const viewerContext = isAdminLike(contextUser) ? 'admin' : 'staff';
  const isStaff = viewerContext === 'staff';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [progress, setProgress] = useState(() => readWalkthroughProgress(viewerContext));
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false));
  const [anchorTop, setAnchorTop] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setProgress(readWalkthroughProgress(viewerContext));
    if (isMobile && wrapRef.current) setAnchorTop(wrapRef.current.getBoundingClientRect().bottom + 8);
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, viewerContext, isMobile]);

  const eventId = eventIdFromPath(location.pathname);
  const pageGuides = useMemo(() => guidesForRoute(location.pathname), [location.pathname]);
  const results = useMemo(() => (query.trim() ? searchGuides(query) : pageGuides), [query, pageGuides]);
  const tour = pageTour(location.pathname, viewerContext);

  const onStart = (id) => { startGuide(id, eventId, eventName); setOpen(false); };
  const onTour = () => { if (tour.trigger) { try { window.dispatchEvent(new CustomEvent(tour.trigger)); } catch { /* no-op */ } } setOpen(false); };
  const onProgress = () => { try { window.dispatchEvent(new CustomEvent('staffed:certificate:open')); } catch { /* no-op */ } setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        data-testid="help-button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, borderRadius: 9999,
          border: `1px solid ${open ? GOLD : 'rgba(212,175,55,0.4)'}`, background: 'rgba(10,12,16,0.72)',
          color: '#e9d9a0', padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          backdropFilter: 'blur(6px)', boxShadow: '0 6px 18px rgba(0,0,0,0.4)', whiteSpace: 'nowrap',
        }}
        title="Help"
      >
        <HelpCircle className="w-3.5 h-3.5" style={{ flexShrink: 0 }} />
        <span className="hidden md:inline">Help</span>
      </button>

      {open && (
        <div
          data-testid="help-panel"
          style={{
            ...(isMobile
              ? { position: 'fixed', top: anchorTop, left: 16, right: 16, width: 'auto', maxWidth: 'calc(100vw - 32px)' }
              : { position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 320 }),
            zIndex: 1400,
            background: 'linear-gradient(180deg,#12162a,#0a0e18)', border: `1px solid rgba(212,175,55,0.22)`,
            borderRadius: 16, boxShadow: '0 24px 60px -18px rgba(0,0,0,0.8)', padding: 12, color: '#fff',
          }}
        >
          {!isStaff && (
            <>
              <div className="flex items-center gap-2 px-2.5 py-2 rounded-xl mb-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Search className="w-3.5 h-3.5 text-white/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="How do I…?"
                  data-testid="help-search"
                  className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder:text-white/30"
                />
              </div>

              <p className="text-[10px] uppercase tracking-[0.16em] text-white/35 font-semibold px-1 mb-1.5">
                {query.trim() ? 'Guides' : 'Guides for this page'}
              </p>
              <div className="max-h-[240px] overflow-y-auto space-y-1 mb-2" data-testid="help-guide-list">
                {results.length === 0 ? (
                  <p className="text-[12px] text-white/40 px-1 py-2">No guides match “{query}”.</p>
                ) : results.map((g) => (
                  <div key={g.id} className="flex items-center gap-2 rounded-xl px-2.5 py-2" style={{ background: 'rgba(255,255,255,0.03)' }} data-testid={`help-guide-${g.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-medium text-white/85 truncate">{g.title}</p>
                      <p className="text-[10.5px] text-white/35">{g.minutes} min</p>
                    </div>
                    <button type="button" onClick={() => onStart(g.id)} data-testid={`help-start-${g.id}`} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-[#0B0F1A] shrink-0" style={{ background: 'linear-gradient(180deg,#E7C75E,#D4AF37)' }}>
                      Start <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {!query.trim() && pageGuides.length === 0 && (
                  <p className="text-[12px] text-white/40 px-1 py-1.5">No guides for this page — search above for anything else.</p>
                )}
              </div>
            </>
          )}

          <div className="flex items-center gap-2 mb-2">
            {tour.available && (
              <button type="button" onClick={onTour} data-testid="help-tour-page" className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[12px] font-semibold text-[#E7C96A]" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)' }}>
                <PlayCircle className="w-3.5 h-3.5" /> Tour this page
              </button>
            )}
          </div>

          {progress.total > 0 && (
            <button type="button" onClick={onProgress} data-testid="help-progress-line" className="w-full flex items-center gap-2 rounded-xl px-2.5 py-2 text-left" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <Award className="w-3.5 h-3.5 text-[#E7C96A]" />
              <span className="text-[11.5px] text-white/55 flex-1">
                {progress.isComplete ? 'STAFFED PRO — all walkthroughs explored' : `${progress.completed} of ${progress.total} pages explored`}
              </span>
              <ArrowRight className="w-3 h-3 text-white/30" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export { GUIDES };
